import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, User, MapPin, Camera, ChevronRight, ChevronLeft, Loader2, Plus, X, Sparkles, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const INTERESTS = [
  'Rejser', 'Musik', 'Film', 'Læsning', 'Sport', 'Fitness', 'Madlavning', 'Kunst',
  'Fotografering', 'Gaming', 'Natur', 'Kæledyr', 'Kaffe', 'Vin', 'Dans', 'Yoga',
  'Vandreture', 'Strand', 'Teknologi', 'Mode', 'Mad', 'Koncerter', 'Museer', 'Natteliv'
];

const ProfileSetup = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profile, setProfile] = useState({
    bio: '',
    age: '',
    gender: '',
    looking_for: '',
    location: '',
    interests: [],
    photos: []
  });
  const fileInputRef = useRef(null);

  const { token, updateUser } = useAuth();
  const navigate = useNavigate();

  const handleInterestToggle = (interest) => {
    if (profile.interests.includes(interest)) {
      setProfile({ ...profile, interests: profile.interests.filter(i => i !== interest) });
    } else if (profile.interests.length < 10) {
      setProfile({ ...profile, interests: [...profile.interests, interest] });
    }
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Ugyldig filtype. Kun JPEG, PNG, WebP og GIF er tilladt.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Filen er for stor. Maksimum 5MB.');
      return;
    }

    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await axios.post(`${API}/upload/photo`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setProfile({ ...profile, photos: res.data.photos });
      toast.success('Billede uploadet!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke uploade billede');
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handlePhotoRemove = async (photoUrl) => {
    try {
      const res = await axios.delete(`${API}/upload/photo?photo_url=${encodeURIComponent(photoUrl)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile({ ...profile, photos: res.data.photos });
      toast.success('Billede slettet');
    } catch (err) {
      toast.error('Kunne ikke slette billede');
    }
  };

  const handlePhotoAdd = () => {
    // For demo, using placeholder images when real upload is not available
    const demoPhotos = [
      'https://images.pexels.com/photos/33534041/pexels-photo-33534041.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      'https://images.pexels.com/photos/33871730/pexels-photo-33871730.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    ];
    const randomPhoto = demoPhotos[profile.photos.length % 2];
    if (profile.photos.length < 6) {
      setProfile({ ...profile, photos: [...profile.photos, randomPhoto] });
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.put(`${API}/profile`, {
        ...profile,
        age: parseInt(profile.age) || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      updateUser(res.data);
      toast.success('Profil fuldført! Lad os finde dit match.');
      navigate('/discover');
    } catch (err) {
      toast.error('Kunne ikke gemme profil');
    } finally {
      setLoading(false);
    }
  };

  const steps = [
    { title: 'Basis Info', icon: <User className="w-5 h-5" /> },
    { title: 'Din Bio', icon: <Heart className="w-5 h-5" /> },
    { title: 'Interesser', icon: <Sparkles className="w-5 h-5" /> },
    { title: 'Billeder', icon: <Camera className="w-5 h-5" /> }
  ];

  const canProceed = () => {
    switch (step) {
      case 1: return profile.age && profile.gender && profile.looking_for;
      case 2: return profile.bio.length >= 10;
      case 3: return profile.interests.length >= 3;
      case 4: return profile.photos.length >= 1;
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Progress Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-background border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary fill-primary" />
              <span className="text-lg font-display font-semibold text-foreground">Profil Opsætning</span>
            </div>
            <span className="text-sm text-muted-foreground">Trin {step} af 4</span>
          </div>
          <div className="flex gap-2">
            {steps.map((s, i) => (
              <div
                key={i}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  i + 1 <= step ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-28 pb-24 px-4">
        <div className="max-w-lg mx-auto">
          <AnimatePresence mode="wait">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground">Lad os komme i gang</h2>
                  <p className="text-muted-foreground mt-1">Fortæl os lidt om dig selv</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Din alder</Label>
                    <Input
                      type="number"
                      placeholder="25"
                      value={profile.age}
                      onChange={(e) => setProfile({ ...profile, age: e.target.value })}
                      className="h-12 rounded-xl"
                      min={18}
                      max={100}
                      data-testid="setup-age-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Jeg er</Label>
                    <Select value={profile.gender} onValueChange={(v) => setProfile({ ...profile, gender: v })}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="setup-gender-select">
                        <SelectValue placeholder="Vælg dit køn" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Mand</SelectItem>
                        <SelectItem value="female">Kvinde</SelectItem>
                        <SelectItem value="other">Andet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Søger</Label>
                    <Select value={profile.looking_for} onValueChange={(v) => setProfile({ ...profile, looking_for: v })}>
                      <SelectTrigger className="h-12 rounded-xl" data-testid="setup-looking-for-select">
                        <SelectValue placeholder="Hvem leder du efter?" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Mænd</SelectItem>
                        <SelectItem value="female">Kvinder</SelectItem>
                        <SelectItem value="both">Alle</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Lokation</Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                      <Input
                        placeholder="København, Danmark"
                        value={profile.location}
                        onChange={(e) => setProfile({ ...profile, location: e.target.value })}
                        className="h-12 pl-10 rounded-xl"
                        data-testid="setup-location-input"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Step 2: Bio */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground">Fortæl din historie</h2>
                  <p className="text-muted-foreground mt-1">Lad andre vide hvad der gør dig unik</p>
                </div>

                <div className="space-y-2">
                  <Label>Om mig</Label>
                  <Textarea
                    placeholder="Jeg er en kaffeentusiast der elsker at udforske nye steder..."
                    value={profile.bio}
                    onChange={(e) => setProfile({ ...profile, bio: e.target.value })}
                    className="min-h-[200px] rounded-xl resize-none"
                    maxLength={500}
                    data-testid="setup-bio-input"
                  />
                  <div className="text-right text-sm text-muted-foreground">
                    {profile.bio.length}/500
                  </div>
                </div>

                <div className="bg-muted/50 rounded-xl p-4">
                  <h4 className="font-medium text-foreground mb-2">Tips til en god bio:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Vær autentisk og ægte</li>
                    <li>• Nævn dine hobbyer og passioner</li>
                    <li>• Del hvad du leder efter</li>
                    <li>• Hold det positivt og engagerende</li>
                  </ul>
                </div>
              </motion.div>
            )}

            {/* Step 3: Interests */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground">Dine interesser</h2>
                  <p className="text-muted-foreground mt-1">
                    Vælg mindst 3 interesser ({profile.interests.length}/10)
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  {INTERESTS.map((interest) => (
                    <Badge
                      key={interest}
                      variant={profile.interests.includes(interest) ? 'default' : 'outline'}
                      className={`cursor-pointer px-4 py-2 text-sm rounded-full transition-all ${
                        profile.interests.includes(interest)
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'hover:bg-muted'
                      }`}
                      onClick={() => handleInterestToggle(interest)}
                      data-testid={`interest-${interest.toLowerCase()}`}
                    >
                      {interest}
                    </Badge>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 4: Photos */}
            {step === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div>
                  <h2 className="text-2xl font-display font-bold text-foreground">Tilføj dine billeder</h2>
                  <p className="text-muted-foreground mt-1">
                    Tilføj mindst 1 billede for at fuldføre din profil
                  </p>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                />

                <div className="grid grid-cols-3 gap-3">
                  {profile.photos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-xl overflow-hidden group">
                      <img src={photo.startsWith('/api') ? `${API.replace('/api', '')}${photo}` : photo} alt={`Billede ${index + 1}`} className="w-full h-full object-cover" />
                      <button
                        onClick={() => handlePhotoRemove(photo)}
                        className="absolute top-2 right-2 w-6 h-6 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                      {index === 0 && (
                        <div className="absolute bottom-2 left-2 bg-primary text-white text-xs px-2 py-1 rounded-full">
                          Hovedbillede
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {profile.photos.length < 6 && (
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="aspect-square rounded-xl border-2 border-dashed border-border hover:border-primary/50 flex flex-col items-center justify-center gap-2 text-muted-foreground hover:text-primary transition-colors disabled:opacity-50"
                      data-testid="add-photo-btn"
                    >
                      {uploadingPhoto ? (
                        <Loader2 className="w-6 h-6 animate-spin" />
                      ) : (
                        <>
                          <Upload className="w-6 h-6" />
                          <span className="text-xs">Upload billede</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                <div className="bg-muted/50 rounded-xl p-4">
                  <h4 className="font-medium text-foreground mb-2">Billedetips:</h4>
                  <ul className="text-sm text-muted-foreground space-y-1">
                    <li>• Dit første billede bliver dit hovedbillede</li>
                    <li>• Brug klare, velbelyste billeder</li>
                    <li>• Vis dit ansigt tydeligt</li>
                    <li>• Maksimum 5MB per billede</li>
                  </ul>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4">
        <div className="max-w-lg mx-auto flex gap-3">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="flex-1 h-12 rounded-full"
              data-testid="setup-back-btn"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Tilbage
            </Button>
          )}
          
          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90"
              data-testid="setup-next-btn"
            >
              Fortsæt
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={!canProceed() || loading}
              className="flex-1 h-12 rounded-full bg-primary hover:bg-primary/90"
              data-testid="setup-complete-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Fuldfør Profil
                  <Heart className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileSetup;
